import type { Auth } from 'firebase/auth';
import {
  type Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import type { DeclarationAnswer } from '../../features/declarations/domain/declaration/schema';
import type {
  DeclarationPort,
  MyDeclaration,
} from '../../features/declarations/application/ports/declarationPort';
import { DeclarationError } from '../../features/declarations/application/ports/declarationPort';

/**
 * Firestore implementation of `DeclarationPort` (blueprint §11.1).
 *
 * The main document and the medical subcollection are written in one batch, so
 * a saved declaration never ends up with answers in one and not the other.
 */

function translate(error: unknown): DeclarationError {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

  if (code.includes('permission-denied')) {
    // The rules refuse when submissions are off or the declaration has moved
    // past the employee's control. Both look the same from here, so the message
    // covers them honestly rather than guessing which.
    return new DeclarationError(
      'not_editable',
      'لا يمكن التعديل الآن. قد تكون فترة التقديم مغلقة أو تمت مراجعة إقرارك.',
    );
  }
  if (code.includes('unavailable') || code.includes('deadline')) {
    return new DeclarationError('network', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }
  return new DeclarationError('unknown', 'تعذّر إتمام العملية. حاول مرة أخرى.');
}

function readAnswers(value: unknown): readonly DeclarationAnswer[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry): DeclarationAnswer[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const itemId = record['itemId'];
    const answer = record['value'];

    if (typeof itemId !== 'string') return [];
    if (
      typeof answer === 'boolean' ||
      typeof answer === 'string' ||
      typeof answer === 'number' ||
      Array.isArray(answer)
    ) {
      return [{ itemId, value: answer as DeclarationAnswer['value'] }];
    }
    return [];
  });
}

export function createFirebaseDeclarationAdapter(
  firestore: Firestore,
  auth: Auth,
): DeclarationPort {
  async function resolveMyEmployeeId(): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new DeclarationError('forbidden', 'يجب تسجيل الدخول أولًا.');

    const link = await getDoc(doc(firestore, 'userAccounts', uid));
    const employeeId: unknown = link.data()?.['employeeId'];

    if (typeof employeeId !== 'string') {
      throw new DeclarationError('forbidden', 'لا يوجد سجل موظف مرتبط بحسابك.');
    }
    return employeeId;
  }

  /** Deterministic id: one declaration per employee per policy year. */
  function declarationId(employeeId: string, policyYear: string): string {
    return `${employeeId}__${policyYear}`;
  }

  return {
    async getMyDeclaration(policyYear: string): Promise<MyDeclaration | null> {
      try {
        const employeeId = await resolveMyEmployeeId();
        const id = declarationId(employeeId, policyYear);

        const main = await getDoc(doc(firestore, 'declarations', id));
        if (!main.exists()) return null;

        const data = main.data();

        // Bounded: one medical document per policy year by construction, but
        // the limit is stated rather than assumed (§11.5.1).
        const medical = await getDocs(
          query(collection(firestore, 'declarations', id, 'medical'), limit(5)),
        );

        const medicalAnswers = medical.docs.flatMap((entry) =>
          readAnswers(entry.data()['answers']),
        );

        // Firestore's DocumentData is indexed `any`; each read is narrowed.
        const status: unknown = data['status'];
        const submittedAt: unknown = data['submittedAt'];
        const note: unknown = data['reviewNoteAr'];

        return {
          id,
          policyYear,
          status: (typeof status === 'string' ? status : 'draft') as MyDeclaration['status'],
          answers: readAnswers(data['answers']),
          medicalAnswers,
          submittedAt: typeof submittedAt === 'string' ? submittedAt : null,
          reviewNoteAr: typeof note === 'string' ? note : null,
        };
      } catch (error) {
        throw error instanceof DeclarationError ? error : translate(error);
      }
    },

    async saveDraft(policyYear, answers, medicalAnswers): Promise<void> {
      try {
        const employeeId = await resolveMyEmployeeId();
        const id = declarationId(employeeId, policyYear);
        const now = new Date().toISOString();

        const mainRef = doc(firestore, 'declarations', id);
        const medicalRef = doc(firestore, 'declarations', id, 'medical', policyYear);
        const existing = await getDoc(mainRef);

        // One batch, so the main document and the medical subcollection cannot
        // end up describing different declarations.
        const batch = writeBatch(firestore);

        if (existing.exists()) {
          // `answers` and `updatedAt` only — exactly what the rules allow an
          // employee to change on an existing declaration.
          batch.update(mainRef, { answers, updatedAt: now });
        } else {
          batch.set(mainRef, {
            schemaVersion: 1,
            id,
            employeeId,
            policyYear,
            status: 'draft',
            answers,
            createdAt: now,
            updatedAt: now,
          });
        }

        batch.set(medicalRef, {
          schemaVersion: 1,
          declarationId: id,
          employeeId,
          answers: medicalAnswers,
          updatedAt: now,
        });

        await batch.commit();
      } catch (error) {
        throw error instanceof DeclarationError ? error : translate(error);
      }
    },

    async submit(): Promise<void> {
      try {
        const employeeId = await resolveMyEmployeeId();

        // Finds the caller's own declaration without needing the policy year
        // passed in again; bounded to one.
        const found = await getDocs(
          query(
            collection(firestore, 'declarations'),
            where('employeeId', '==', employeeId),
            limit(1),
          ),
        );

        const target = found.docs[0];
        if (!target) {
          throw new DeclarationError('not_editable', 'لا يوجد إقرار لإرساله.');
        }

        const now = new Date().toISOString();

        // Exactly the three fields the rules permit on submission. Sending the
        // answers here too would be refused — submitting is a status change,
        // not another save.
        await updateDoc(doc(firestore, 'declarations', target.id), {
          status: 'submitted',
          submittedAt: now,
          updatedAt: now,
        });
      } catch (error) {
        throw error instanceof DeclarationError ? error : translate(error);
      }
    },
  };
}
